import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, ArrowRight, Sparkles, Loader2 } from "lucide-react";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import LanguageSelector from "../components/LanguageSelector";
import InstallAppCard from "../components/InstallAppCard";
import CountrySelect from "../components/CountrySelect";
import { DEFAULT_COUNTRY } from "../data/countries";
import useMsg91Widget, { buildIdentifier } from "../hooks/useMsg91Widget";

function errText(e) {
  if (!e) return "Something went wrong";
  if (typeof e === "string") return e;
  const raw =
    e?.response?.data?.detail ||
    e?.message ||
    e?.error ||
    "Something went wrong";
  // MSG91 abuse-protection returns "IPBlocked" — soften it for end users.
  if (/IPBlocked/i.test(raw)) {
    return "Too many attempts from this network. Try after 1 hour.";
  }
  return raw;
}

/** Parse "wait N seconds to retry." from MSG91 error message. */
function parseWaitSeconds(msg) {
  const m = /wait\s+(\d+)\s*seconds?/i.exec(String(msg || ""));
  return m ? parseInt(m[1], 10) : null;
}

export default function Login() {
  const { login, lang, config } = useAuth();
  const navigate = useNavigate();

  const widget = useMsg91Widget({
    widgetId: config?.msg91_widget_id,
    tokenAuth: config?.msg91_token_auth,
    captchaRenderId: "captcha-container",
  });

  const [step, setStep] = useState("phone");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const otpLength = config?.otp_length || 6;
  const defaultCooldown = config?.otp_resend_cooldown_seconds || 30;

  const startCooldown = (sec) => {
    setCooldown(sec);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };
  useEffect(() => () => cooldownRef.current && clearInterval(cooldownRef.current), []);

  const validPhone = () => /^\d{6,14}$/.test(phone);

  const handleSendOtp = async () => {
    if (!validPhone()) return toast.error("Please enter a valid mobile number");
    if (!widget.ready) {
      // eslint-disable-next-line no-console
      console.warn("[MSG91] sendOtp clicked before widget ready");
      return toast.error("OTP service is still loading — please wait a moment");
    }

    const identifier = buildIdentifier(country.dial, phone);
    if (!identifier) return toast.error("Invalid phone number format");

    setLoading(true);
    try {
      await widget.sendOtp(identifier);
      toast.success(`OTP sent to ${country.dial} ${phone}`);
      startCooldown(defaultCooldown);
      setStep("otp");
    } catch (e) {
      const msg = errText(e);
      const waitSec = parseWaitSeconds(msg);
      if (waitSec) {
        toast.error(`Please wait ${waitSec}s before trying again`);
        startCooldown(waitSec);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0 || resending) return;
    if (!widget.ready) return toast.error("OTP service is still loading");
    setResending(true);
    try {
      await widget.retryOtp("11"); // 11 = SMS via MSG91
      toast.success("OTP resent");
      startCooldown(defaultCooldown);
    } catch (e) {
      const msg = errText(e);
      const waitSec = parseWaitSeconds(msg);
      if (waitSec) {
        toast.error(`Please wait ${waitSec}s before requesting another OTP`);
        startCooldown(waitSec);
      } else {
        toast.error(msg);
      }
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== otpLength) return toast.error(`Enter the ${otpLength}-digit OTP`);
    if (!widget.ready) return toast.error("OTP service is still loading");

    setLoading(true);
    try {
      const data = await widget.verifyOtp(otp);
      // MSG91 returns the verified access token in `message`.
      const accessToken =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.access_token ||
        data?.["access-token"] ||
        data?.token;

      if (!accessToken || typeof accessToken !== "string") {
        // eslint-disable-next-line no-console
        console.error("[MSG91] verifyOtp returned no access token:", data);
        throw new Error("OTP verified but no access token returned");
      }

      const r = await api.post("/auth/msg91/verify-access-token", {
        access_token: accessToken,
        phone,
        country_dial_code: country.dial,
      });
      login(r.data.token, r.data.user);
      toast.success(r.data.message);
      navigate("/");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp("");
    setCooldown(0);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  };

  const sendDisabled = loading || !validPhone() || !widget.ready;

  return (
    <div className="relative min-h-screen">
      <div className="page-shell relative z-10 flex flex-col">
      <div className="flex justify-end mb-2">
        <LanguageSelector />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center text-center mt-4"
      >
        {/* App-icon sparkle circle — same as everywhere in the app */}
        <motion.div
          animate={{ rotate: [0, -8, 8, -8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-28 h-28 rounded-full bg-gradient-to-br from-yellow-300 to-pink-400 flex items-center justify-center shadow-[0_8px_0_rgba(0,0,0,0.12)] border-4 border-white"
        >
          <Sparkles size={52} strokeWidth={2.5} className="text-white" />
        </motion.div>

        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-6 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "platform_name")}
        </h1>
        <p className="text-base sm:text-lg text-cyan-50/95 mt-2 font-semibold max-w-[85%] leading-snug drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "platform_tagline")}
        </p>
      </motion.div>

      <div className="mt-8 relative rounded-3xl p-6 border border-white/40 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.5)]
        bg-white/85 backdrop-blur-xl">
        <AnimatePresence mode="wait">
          {step === "phone" ? (
            <motion.div
              key="phone"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <label className="font-heading text-xl font-bold text-slate-700 flex items-center gap-2">
                <Phone size={22} strokeWidth={2.5} className="text-sky-500" />
                {t(lang, "enter_phone")}
              </label>
              <div className="mt-4 flex items-stretch gap-2 min-w-0">
                <CountrySelect value={country} onChange={setCountry} />
                <input
                  data-testid="phone-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={14}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder={t(lang, "phone_placeholder")}
                  className="flex-1 min-w-0 w-full px-4 h-14 text-xl font-bold text-slate-900 bg-[#f8fafc] outline-none placeholder:text-slate-400 rounded-2xl border-2 border-slate-900/10 focus:border-sky-400 focus:bg-white transition-colors"
                />
              </div>
              <button
                data-testid="send-otp-btn"
                disabled={sendDisabled}
                onClick={handleSendOtp}
                className="mt-6 w-full bg-pink-400 hover:bg-pink-500 disabled:bg-white/90 disabled:text-slate-400 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2 shadow-[0_4px_18px_-4px_rgba(15,23,42,0.35)]"
              >
                {loading ? (
                  <Loader2 size={22} strokeWidth={3} className="animate-spin" />
                ) : !widget.ready ? (
                  <>
                    <Loader2 size={18} strokeWidth={3} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    {t(lang, "send_otp")} <ArrowRight size={22} strokeWidth={3} />
                  </>
                )}
              </button>
              {widget.error && (
                <p data-testid="widget-error" className="mt-2 text-center text-xs text-rose-500 font-semibold">
                  {widget.error}
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="otp"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <label className="font-heading text-xl font-bold text-slate-700">
                {t(lang, "enter_otp")}
              </label>
              <p className="text-xs text-slate-500 mt-1 font-semibold">
                Sent to {country.dial} {phone}
              </p>
              <div className="mt-4 relative">
                <input
                  data-testid="otp-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={otpLength}
                  value={otp}
                  onChange={() => { /* handled by onInput for cross-browser autofill */ }}
                  onInput={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, "").slice(0, otpLength);
                    setOtp(cleaned);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, otpLength);
                    setOtp(pasted);
                  }}
                  autoComplete="one-time-code"
                  aria-label={t(lang, "enter_otp")}
                  className="otp-hidden-input absolute inset-0 w-full h-full opacity-100 outline-none border-4 border-slate-900/10 focus:border-sky-400 rounded-2xl bg-transparent z-10"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none flex items-center justify-evenly px-3 py-4 rounded-2xl border-4 border-transparent"
                >
                  {Array.from({ length: otpLength }).map((_, i) => (
                    <span
                      key={i}
                      className={`inline-block text-3xl font-bold leading-none ${
                        otp[i] ? "text-slate-800" : "text-slate-300"
                      }`}
                    >
                      {otp[i] || "●"}
                    </span>
                  ))}
                </div>
              </div>
              <button
                data-testid="verify-otp-btn"
                disabled={loading || otp.length !== otpLength || !widget.ready}
                onClick={handleVerifyOtp}
                className="mt-6 w-full bg-emerald-400 hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={22} strokeWidth={3} className="animate-spin" /> : t(lang, "verify")}
              </button>

              <div className="mt-4 flex items-center justify-between text-sm">
                <button
                  data-testid="change-number-btn"
                  onClick={handleChangeNumber}
                  className="text-slate-500 font-semibold underline"
                >
                  {t(lang, "change_number")}
                </button>
                <button
                  data-testid="resend-otp-btn"
                  onClick={handleResendOtp}
                  disabled={cooldown > 0 || resending || !widget.ready}
                  className="text-pink-500 font-bold disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "..." : "Resend OTP"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* MSG91 captcha container — empty when captcha disabled in dashboard. */}
        <div id="captcha-container" className="mt-3" />
      </div>

      <p className="mt-6 text-center text-xs text-cyan-50/90 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
        New users get {config?.currency_symbol || "₹"}{config?.free_wallet_credit ?? 100} free wallet credit 🎁
      </p>

      <InstallAppCard lang={lang} />

      <div className="mt-3 mb-2 text-center">
        <button
          data-testid="privacy-policy-link"
          onClick={() => navigate("/privacy-policy")}
          className="text-xs text-cyan-50/85 hover:text-white font-semibold underline underline-offset-4 decoration-cyan-200/60 active:translate-y-0.5 transition"
        >
          {t(lang, "privacy_policy")}
        </button>
      </div>
      </div>
    </div>
  );
}
